import { ArrowLeft, Download, ChevronDown, FileDown, Image, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEffect, useRef, useState } from 'react';
import { SearchParam, TableType } from '@common/ts';
import { getErrorMessage, getErrorStatus } from '@common/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCurrentConversations } from '@/hooks/useCurrentConversations';
import { useTranscriptNavigation } from '@/hooks/useTranscriptNavigation';
import { getSortedTranslatedLanguages } from '@/utils/getSortedTranslatedLanguages';
import { useMutateGenerateTranscriptsTranslation } from '@/reactQuery/post';
import useTranslation from '@/hooks/useTranslation';
import TranscriptsTranslationDropdown from './transcripts_table_data/TranscriptsTranslationDropdown';
import languagesList from '@/lang/languages/languagesList';
import ConfirmTranslationModal from '@/components/Modals/ConfirmTranslationModal';
import { TranscriptProps } from '@/types';
import { ConversationNavigationType, TranscriptsView, NavigationTabs } from '@/enums';
import useTranslationsLoadingStore from '@/store/TranslationLoadingStore';
import useResultTabStore from '@/store/ResultsTabStore';
import ExportModal from '@/components/Modals/ExportModal/ExportModal';
import {
  useMutateGetConversationsPageWithStore,
  useGetConversation,
  useMutateDownloadConversationMedia,
} from '@/reactQuery/get';
import NavigationControls from '@/components/NavigationControls/NavigationControls';
import ConversationChat from '@/components/ConversationChat/ConversationChat';
import ConversationMetadata from '@/components/ConversationMetadata/ConversationMetadata';
import TranscriptInfoStrip from '@/components/ConversationMetadata/TranscriptInfoStrip';
import { getConversationId } from '@/utils/getTranscriptId';
import { getPaginationParamPage } from '@/utils/urlParams';

const MAX_SUMMARY = 80;

function SingleTranscriptData({
  completedConversationsLength,
  languages,
  projectId,
  initialTranslatedLanguages,
  onScrollTo,
  urlCurrentPage,
  tableType,
  activeView,
}: TranscriptProps) {
  const { lang } = useTranslation();
  const { setActiveTab } = useResultTabStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const translationLanguage = searchParams.get(SearchParam.TRANSLATION_LANGUAGE);
  const conversationFromQuestionsTab =
    searchParams.get(SearchParam.CONVERSATION_FROM_QUESTIONS_TAB) === 'true';
  const withoutNavigation = conversationFromQuestionsTab;
  const activeLanguage = translationLanguage || initialTranslatedLanguages[0];
  const [dropdownLanguage, setDropdownLanguage] = useState<string | null>(null);
  const [translatedLanguages] = useState<string[]>(initialTranslatedLanguages);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unTranslatedLanguages] = useState<string[]>(
    languagesList.language
      .map(({ label }) => label)
      .filter((language) => !languages.includes(language) && language !== activeLanguage),
  );
  const isCompletedTranscriptsView = activeView === TranscriptsView.COMPLETED;

  // Use navigation hook
  const {
    conversationId,
    isFirstConversation,
    isLastConversation,
    isGetConversationsPagePending,
    removeSelectedTranscriptAndGoBack,
    handleNextTranscript,
    handlePrevTranscript,
    direction,
  } = useTranscriptNavigation({
    projectId: projectId!,
    activeView,
    urlCurrentPage,
    tableType,
    onScrollTo,
  });

  const { setTranslatingProjects, translatingProjects } = useTranslationsLoadingStore();

  const isEnabledFetch = useRef(true);
  const { mutateAsync } = useMutateGenerateTranscriptsTranslation(projectId);
  const { mutateAsync: mutateGetConversationsPage } = useMutateGetConversationsPageWithStore(
    isCompletedTranscriptsView,
    urlCurrentPage,
    true,
  );
  const {
    selectedTranscript,
    actions: { setSelectedTranscript },
    utils: { getTranscriptById },
  } = useCurrentConversations();
  // Get transcript from store first
  const storeTranscript = getTranscriptById(conversationId);

  // Only fetch from API if coming from questions tab AND not already in store
  const shouldFetchFromApi = conversationFromQuestionsTab || !storeTranscript;

  // Get conversation from API when coming from questions tab
  const { data: conversationFromApi } = useGetConversation(
    projectId!,
    shouldFetchFromApi ? conversationId : '',
  );
  // Use store data if available, otherwise use API data when coming from questions tab
  const effectiveTranscript = storeTranscript || conversationFromApi;

  // Media download
  const { mutateAsync: downloadMedia, isPending: isDownloadingMedia } =
    useMutateDownloadConversationMedia();

  const hasMediaFiles =
    effectiveTranscript?.messages?.some(
      (msg) =>
        msg.role === 'user' &&
        msg.data?.some((d: { file_id?: string }) => d.file_id && !d.file_id.includes('/projects/')),
    ) ?? false;

  const handleDownloadMedia = async () => {
    if (!effectiveTranscript?.project_id || !effectiveTranscript?._id) return;
    try {
      const filename = `conversation_${effectiveTranscript._id}.zip`;
      await downloadMedia({
        projectId: effectiveTranscript.project_id,
        conversationId: effectiveTranscript._id,
        filename,
      });
      toast.success(lang.get('msg.downloadStarted') || 'Download started');
    } catch (error: unknown) {
      // TELV1-307 — axiosInstance unwraps `error.response`, so reading
      // `err.response.status` directly was undefined. `getErrorStatus`
      // walks both shapes.
      if (getErrorStatus(error) === 404) {
        toast.error(lang.get('msg.noMediaFound') || 'No media files found for this conversation');
      } else {
        toast.error(lang.get('msg.downloadFailed') || 'Failed to download media');
      }
    }
  };

  // Set selected transcript when conversationId changes
  useEffect(() => {
    if (conversationId && effectiveTranscript) {
      setSelectedTranscript(effectiveTranscript);
    }
  }, [conversationId, effectiveTranscript, setSelectedTranscript, translationLanguage]);

  const currentProjectIsTranslating =
    translatingProjects?.find((p) => p.projectId === projectId && p.source === 'transcripts') ||
    null;

  const handleLanguageSelection = async (language: string) => {
    if (translatedLanguages.includes(language) || language === initialTranslatedLanguages[0]) {
      const newSearchParams = new URLSearchParams(searchParams);

      if (language === initialTranslatedLanguages[0]) {
        newSearchParams.delete(SearchParam.TRANSLATION_LANGUAGE);
      } else {
        newSearchParams.set(SearchParam.TRANSLATION_LANGUAGE, language);
      }

      setSearchParams(newSearchParams);

      // Fetch conversations with the new language parameter
      try {
        const targetLanguage = language === initialTranslatedLanguages[0] ? null : language;
        const result = await mutateGetConversationsPage({
          projectId: projectId!,
          filterId: searchParams.get(SearchParam.FILTER_ID),
          language: targetLanguage,
          conversationFilter: activeView,
          page: urlCurrentPage,
        });

        const sourceTable = targetLanguage
          ? TableType.TRANSCRIPTS
          : TableType.TRANSLATED_TRANSCRIPTS;
        const targetTable = targetLanguage
          ? TableType.TRANSLATED_TRANSCRIPTS
          : TableType.TRANSCRIPTS;

        // Get current page for the active table
        const currentPage = searchParams.get(getPaginationParamPage(sourceTable));

        // Update or delete the target pagination param accordingly
        if (currentPage) {
          newSearchParams.set(getPaginationParamPage(targetTable), currentPage);
        } else {
          newSearchParams.delete(getPaginationParamPage(targetTable));
        }

        setSearchParams(newSearchParams);

        if (result.items && result.items.length > 0) {
          const currentTranscript = result.items.find(
            (item) => getConversationId(item) === selectedTranscript?._id,
          );
          if (currentTranscript) {
            setSelectedTranscript(currentTranscript);
          }
        }
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        toast.error(errorMessage);
      }
    } else {
      isEnabledFetch.current = false;
      setDropdownLanguage(language);
      setIsModalOpen(true);
    }
  };

  const confirmTranslation = async () => {
    if (!dropdownLanguage || !completedConversationsLength) return;
    setIsModalOpen(false);
    toast.info(lang.get('msg.translationMightTakeAWhile'));
    setTranslatingProjects((prev) => [
      ...prev,
      {
        projectId,
        language: dropdownLanguage,
        length: completedConversationsLength,
        source: 'transcripts',
      },
    ]);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(SearchParam.SELECTED_TRANSCRIPT);
    newSearchParams.delete(SearchParam.CONVERSATION_FROM_QUESTIONS_TAB);
    newSearchParams.set(SearchParam.TRANSLATION_LANGUAGE, dropdownLanguage);
    setSearchParams(newSearchParams);
    try {
      await mutateAsync({ languages: [dropdownLanguage] });
    } catch (error) {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
      setTranslatingProjects((prev) =>
        prev.filter((p) => !(p.projectId === projectId && p.source === 'transcripts')),
      );
    }
  };

  const isFetchingPrevConversation =
    isGetConversationsPagePending && direction === ConversationNavigationType.PREV;
  const isFetchingNextConversation =
    isGetConversationsPagePending && direction === ConversationNavigationType.NEXT;
  const isPrevDisabled = isFirstConversation;
  const isNextDisabled = isLastConversation;

  return (
    <>
      <div className="flex flex-col w-full">
        <div className="flex items-center justify-between w-full mb-4 pt-4 gap-3">
          <button
            onClick={() => {
              if (conversationFromQuestionsTab) {
                // Navigate back to the Questions tab instead of Transcripts list
                setActiveTab(NavigationTabs.QUESTIONS);
              }
              // Pass the from-Questions flag so the `tab` URL param is kept in
              // sync with the store and the drilled-into ?question_id survives,
              // restoring the question + its graph. TELV1-322.
              removeSelectedTranscriptAndGoBack(conversationFromQuestionsTab);
            }}
            type="button"
            title={
              conversationFromQuestionsTab
                ? lang.get('msg.backToQuestions')
                : lang.get('msg.backToOverview')
            }
            aria-label={
              conversationFromQuestionsTab
                ? lang.get('msg.backToQuestions')
                : lang.get('msg.backToOverview')
            }
            className="shrink-0 text-sm text-neutral-600 hover:text-neutral-900 transition-colors flex items-center gap-1.5 md800:h-9 md800:w-9 md800:justify-center md800:rounded-md md800:hover:bg-neutral-100"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" />
            <span className="md800:hidden">
              {conversationFromQuestionsTab
                ? lang.get('msg.backToQuestions')
                : lang.get('msg.backToOverview')}
            </span>
          </button>
          <div className="flex items-center gap-3 md800:gap-2 min-w-0">
            {isCompletedTranscriptsView && (
              <TranscriptsTranslationDropdown
                isLoading={!!currentProjectIsTranslating}
                activeLanguage={activeLanguage! || dropdownLanguage}
                translatedLanguages={getSortedTranslatedLanguages(
                  initialTranslatedLanguages,
                  translatedLanguages,
                )}
                unTranslatedLanguages={unTranslatedLanguages}
                handleLanguageSelection={handleLanguageSelection}
              />
            )}
            {!withoutNavigation && (
              <NavigationControls
                onPrev={handlePrevTranscript}
                onNext={handleNextTranscript}
                isPrevDisabled={isPrevDisabled}
                isNextDisabled={isNextDisabled}
                isPrevLoading={isFetchingPrevConversation}
                isNextLoading={isFetchingNextConversation}
                selectedTranscript={selectedTranscript}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="default"
                  title={lang.get('msg.export')}
                  aria-label={lang.get('msg.export')}
                  className="flex items-center gap-1.5 text-sm shrink-0 md800:px-2.5"
                >
                  <Download className="h-4 w-4" />
                  <span className="md800:hidden">{lang.get('msg.export')}</span>
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setIsExportModalOpen(true)}>
                  <FileDown className="h-4 w-4 mr-2" />
                  <div className="flex flex-col">
                    <span>{lang.get('msg.exportData') || 'Export Transcript'}</span>
                    {translationLanguage && (
                      <span className="text-xs text-neutral-400">
                        Exporting in {translationLanguage}
                      </span>
                    )}
                  </div>
                </DropdownMenuItem>
                {hasMediaFiles && (
                  <DropdownMenuItem onClick={handleDownloadMedia} disabled={isDownloadingMedia}>
                    {isDownloadingMedia ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Image className="h-4 w-4 mr-2" />
                    )}
                    {lang.get('msg.downloadMedia') || 'Download Media'}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {!currentProjectIsTranslating && selectedTranscript && (
          // Responsive layout:
          // At wide (>800px) the chat and metadata sit side-by-side inside
          // a fixed-height container (max-h + overflow-hidden) so each
          // panel scrolls independently.
          // At narrow (≤800px) the two panels stack vertically. The full
          // ConversationMetadata card goes BELOW the chat (it contains
          // the long summary which is better suited to end-of-page), but
          // the essential upfront info (status + duration + total
          // messages + first screening tags) shows as a compact chip
          // strip ABOVE the chat so researchers don't need to scroll to
          // the bottom just to see how long a conversation lasted.
          <div className="flex w-full gap-5 md800:flex-col md800:max-h-none md800:overflow-visible max-h-[calc(100dvh-242px)] min-h-0 overflow-hidden">
            <TranscriptInfoStrip
              conversation={selectedTranscript}
              totalMessages={selectedTranscript?.messages?.length}
              className="hidden md800:flex"
            />
            <ConversationChat className="flex-1 min-w-0" messages={selectedTranscript?.messages} />
            <ConversationMetadata
              conversation={selectedTranscript}
              totalMessages={selectedTranscript?.messages?.length}
              isExpanded={isExpanded}
              onSummaryToggle={setIsExpanded}
              maxSummaryWords={MAX_SUMMARY}
            />
          </div>
        )}
        {(currentProjectIsTranslating || !selectedTranscript) && (
          <div className="flex items-center justify-center w-full h-full min-h-[600px]">
            <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
          </div>
        )}
      </div>
      <ConfirmTranslationModal
        isOpen={isModalOpen}
        onConfirm={confirmTranslation}
        onCancel={async () => {
          setIsModalOpen(false);
          isEnabledFetch.current = true;
          const newSearchParams = new URLSearchParams(searchParams.toString());
          if (activeLanguage === 'Original') {
            newSearchParams.delete(SearchParam.TRANSLATION_LANGUAGE);
          } else {
            newSearchParams.set(SearchParam.TRANSLATION_LANGUAGE, activeLanguage);
          }
          setSearchParams(newSearchParams);
          setDropdownLanguage(activeLanguage);
        }}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        projectId={projectId!}
        transcriptsView={activeView}
      />
    </>
  );
}

export default SingleTranscriptData;
